import { ins } from 'src/api/axios'
import {
    type GetZoneResponse,
    type Zone,
    type ZoneCenter,
    type GetZoneCentersResponse,
    type ParkingAssistantSettings,
    type FeatureCollection,
    type ParkingModeRes,
    type GetAreasByZoneIdResponse,
} from 'src/api/fm/zones/zones.model'
import { getHeaders } from 'src/api/headers'
import { getError } from 'src/api/utils'
import { AREA_TYPES_INCLUDING_BOUNTY } from 'src/components/zones/zone-details/addArea/static'

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/getAllZones API documentation}
 */
export const getAllZones = async (): Promise<Zone[] | Error> => {
    const link = encodeURI('v1/fm/zones')
    const headers = await getHeaders()

    return ins
        .get<{ data: GetZoneResponse[] }>(link, { headers })
        .then(res => res.data.data.map(zone => mapGetZoneResponse(zone)))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/getZoneCenters API documentation}
 */
export const getZoneCenters = async (): Promise<ZoneCenter[] | Error> => {
    const link = encodeURI('v1/fm/zones/centers')
    const headers = await getHeaders()

    return ins
        .get<GetZoneCentersResponse>(link, { headers })
        .then(res => res.data.data)
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/getZoneByID API documentation}
 */
export const getZoneById = async (zoneId: string): Promise<Zone | Error> => {
    const link = encodeURI(`v1/fm/zones/${zoneId}`)
    const headers = await getHeaders()

    return ins
        .get<GetZoneResponse>(link, { headers })
        .then(res => mapGetZoneResponse(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/getParkingAssistantSettings API documentation}
 */
export const getParkingAssistantSettingsPerZone = async (zoneId: string): Promise<ParkingAssistantSettings | Error> => {
    const link = encodeURI(`/v1/fm/safety-control/zone-settings/${zoneId}`)
    const headers = await getHeaders()

    return ins
        .get(link, { headers })
        .then(res => mapFineAmountToFullCurrency(res.data.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/updateParkingAssistantSettings API documentation}
 */
export const updateParkingAssistantSettingsPerZone = async (
    settings: ParkingAssistantSettings,
): Promise<void | Error> => {
    const link = encodeURI(`/v1/fm/safety-control/zone-settings/${settings.zoneId}`)
    const headers = await getHeaders()

    return ins
        .put(link, mapFineAmountToCents(settings), { headers })
        .then(() => undefined)
        .catch(e => getError(e, 'response.data.errors[0]'))
}

/**
 * Related {@link https://warf.voiapp.io/catalog/fm/api/fm-api/definition#/zone%20gateway/getAreasByZoneIDs API documentation}
 */
export const getAreasByZoneId = async (zoneId: string | number): Promise<FeatureCollection | Error> => {
    const link = encodeURI(`v1/fm/zones/${zoneId}/areas`)
    const headers = await getHeaders()
    return ins
        .get<GetAreasByZoneIdResponse>(link, { headers })
        .then(res => mapGetAreasByZoneId(res.data))
        .catch(e => getError(e, 'response.data.errors[0]'))
}

const mapGetZoneResponse = (res: GetZoneResponse): Zone => {
    const {
        thresholds,
        polygon,
        companyId,
        companyName,
        country,
        creditsExchangeRate,
        currency,
        defaultLocale,
        displaySpeedUnit,
        feeOutOfZone,
        isActive,
        isTestZone,
        isSleeping,
        language,
        locales,
        maxSpeed,
        name,
        parkingMode,
        parkingSnapshotMode,
        sleepHours,
        supportedVehicleCategories,
        timeZone,
        vat,
        zoneId,
    } = res

    return {
        thresholds,
        polygon,
        companyId,
        companyName,
        country,
        creditsExchangeRate,
        currency,
        defaultLocale,
        displaySpeedUnit,
        feeOutOfZone,
        isActive,
        isTestZone,
        isSleeping,
        language,
        locales,
        maxSpeed,
        name,
        parkingSnapshotMode,
        supportedVehicleCategories: supportedVehicleCategories ?? [],
        timeZone,
        vat,
        minuteCost: 1, // Remove this when zone#PUT request is migrated to FM gateway
        parkingMode: mapParkingMode(parkingMode),
        sleepHours: sleepHours ?? null,
        zone_id: zoneId,
    }
}

const mapParkingMode = (mode: ParkingModeRes) => {
    if (mode === 'free_floating') {
        return 'free-floating'
    }
    if (mode === 'parking_spot') {
        return 'parking-spot'
    }
    if (mode === 'soft_mpz') {
        return 'soft-mpz'
    }

    return mode
}

const mapFineAmountToCents = (settings: ParkingAssistantSettings): ParkingAssistantSettings => {
    return {
        ...settings,
        badPhotoFineAmount: settings.badPhotoFineAmount * 100,
        badParkingFineAmount: settings.badParkingFineAmount * 100,
        noPhotoFineAmount: settings.noPhotoFineAmount * 100,
        abandonedVehicleFineAmount: settings.abandonedVehicleFineAmount * 100,
    }
}

const mapFineAmountToFullCurrency = (settings: ParkingAssistantSettings): ParkingAssistantSettings => {
    return {
        ...settings,
        badPhotoFineAmount: settings.badPhotoFineAmount / 100,
        badParkingFineAmount: settings.badParkingFineAmount / 100,
        noPhotoFineAmount: settings.noPhotoFineAmount / 100,
        abandonedVehicleFineAmount: settings.abandonedVehicleFineAmount / 100,
    }
}

const mapGetAreasByZoneId = (data: GetAreasByZoneIdResponse): FeatureCollection => {
    return {
        ...data,
        // Only known area types can be properly processed in the presentation layer. Therefore we are removing unknown area types.
        features: data.features.filter(feature => {
            return AREA_TYPES_INCLUDING_BOUNTY.includes(feature.properties.area_type)
        }),
    }
}
